import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

function PriceSettings() {
  const [settings, setSettings] = useState({
    under_price_threshold: '1000',
    under_price_title: 'Under',
    under_price_description: 'amazing products under'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "info", msg: "" });
  
  const pageRef = useRef(null);

  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    gsap.fromTo(
      ".toast-anim",
      { y: 12, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.25, ease: "power2.out" }
    );
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2600);
  };

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const response = await $.getJSON(`${ApiURl}/getPriceSettings.php`);
        if (response?.success) {
          setSettings(response.settings);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        showToast("error", "Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // GSAP animation
  useEffect(() => {
    if (!pageRef.current || loading) return;
    
    gsap.fromTo(
      pageRef.current.children,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "power2.out" }
    );
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await $.ajax({
        url: `${ApiURl}/updatePriceSettings.php`,
        type: "POST",
        data: settings,
        dataType: "json"
      });

      if (response?.success) {
        showToast("success", "Settings updated successfully");
      } else {
        showToast("error", response?.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      showToast("error", "Server error while saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Allow any positive number
    if (value === '' || /^\d+$/.test(value)) {
      setSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Ensure value is not empty and is a valid number
    if (value === '' || parseInt(value) < 1) {
      setSettings(prev => ({
        ...prev,
        [name]: '1'
      }));
    }
  };

  if (loading) {
    return (
      <div className="w-full font-['Poppins',sans-serif] bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="w-full font-['Poppins',sans-serif] bg-gray-50 min-h-screen">
      {/* Toast */}
      {toast.show && (
        <div className="toast-anim fixed bottom-5 right-5 z-[9999]">
          <div
            className={[
              "rounded-xl px-4 py-3 shadow-lg border text-sm",
              toast.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : toast.type === "error"
                ? "bg-rose-50 border-rose-200 text-rose-900"
                : "bg-slate-50 border-slate-200 text-slate-900",
            ].join(" ")}
          >
            {toast.msg}
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Price Settings
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Configure the price threshold for the "Under X" section on the home page
          </p>
        </div>

        {/* Settings Form */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <h2 className="text-lg font-semibold text-slate-900">
              Under Price Configuration
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* Price Threshold - FIXED: Removed step constraint */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-900">
                  Price Threshold (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="under_price_threshold"
                  value={settings.under_price_threshold}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="1"
                  max="100000"
                  step="1" // Changed from "100" to "1" to allow any number
                  className="w-full max-w-md rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                  placeholder="e.g., 1000"
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  Products with price less than or equal to this amount will appear in the "Under X" section
                </p>
              
              </div>


              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className={[
                    "inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition",
                    saving 
                      ? "bg-slate-400 cursor-not-allowed" 
                      : "bg-[#b29063] hover:bg-[#a08053] active:scale-[0.99]",
                  ].join(" ")}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setSettings({
                      under_price_threshold: '1000',
                      under_price_title: 'Under',
                      under_price_description: 'amazing products under'
                    });
                  }}
                  className="px-6 py-3 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition"
                >
                  Reset to Default
                </button>
              </div>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}

export default PriceSettings;